/**
 * The five categories the home page puts in front of a buyer, and the leaf
 * subcategories each one draws its cards from.
 *
 * The top-level codes are the same ones CATEGORY_TREE uses, so `/products?cat=`
 * lands on exactly the catalogue slice the card came from. The leaves listed in
 * `featureSubs` are the four we want represented on screen; `allSubs` is the
 * whole category, used as the fallback pool when a featured pick is missing.
 *
 * Only these five are shown, and they are fixed — the products inside them are
 * what changes. Adding a sixth means adding it here and nowhere else.
 */

import { CATEGORY_BY_CODE } from './categoryTree';

// Every leaf code under a top category, flattened out of the taxonomy so this
// file never has to restate it.
const leavesOf = (code) => {
  const cat = CATEGORY_BY_CODE[code];
  if (!cat) return [];
  return cat.sections.flatMap((s) => s.subs.map((x) => x.code));
};

// The still image each card falls back to when its product image fails to load,
// and what the whole row falls back to if the query itself fails. These ship
// with the app, so they cannot 404 the way a storage URL can.
const CATEGORY_IMAGE = {
  furniture:   '/categories/furniture_decor.webp',
  apparel:     '/categories/clothing_footwear.webp',
  home_living: '/categories/restaurant_hospitality.webp',
  building:    '/categories/building_construction.webp',
  auto_parts:  '/categories/auto_parts_equipment.webp',
};

const RAW = [
  {
    code: 'furniture',
    tab:   { ar: 'أثاث', en: 'Furniture', zh: '家具' },
    title: { ar: 'أثاث يناسب تجارتك', en: 'Furniture for your trade', zh: '适合您生意的家具' },
    featureSubs: [
      { code: 'sofas',         ar: 'كنب',            en: 'Sofas',           zh: '沙发' },
      { code: 'tables_chairs', ar: 'طاولات وكراسي',  en: 'Tables & chairs', zh: '桌椅' },
      { code: 'cabinets',      ar: 'خزائن',          en: 'Cabinets',        zh: '柜类' },
      { code: 'bedroom',       ar: 'غرف نوم',        en: 'Bedroom',         zh: '卧室' },
    ],
  },
  {
    code: 'apparel',
    tab:   { ar: 'ملابس', en: 'Apparel', zh: '服装' },
    title: { ar: 'ملابس تبيعها بثقة', en: 'Apparel you can stock', zh: '可放心经销的服装' },
    featureSubs: [
      { code: 'womens_clothing', ar: 'نسائي',  en: "Women's", zh: '女装' },
      { code: 'mens_clothing',   ar: 'رجالي',  en: "Men's",   zh: '男装' },
      { code: 'footwear',        ar: 'أحذية',  en: 'Footwear', zh: '鞋类' },
      { code: 'bags',            ar: 'حقائب',  en: 'Bags',     zh: '箱包' },
    ],
  },
  {
    code: 'home_living',
    tab:   { ar: 'المنزل والمطبخ', en: 'Home & kitchen', zh: '家居厨房' },
    title: { ar: 'للمنزل والمطبخ', en: 'Home & kitchen', zh: '家居与厨房' },
    featureSubs: [
      { code: 'cookware',  ar: 'أدوات الطهي',  en: 'Cookware',  zh: '厨具' },
      { code: 'tableware', ar: 'أدوات المائدة', en: 'Tableware', zh: '餐具' },
      { code: 'drinkware', ar: 'أكواب وفناجين', en: 'Drinkware', zh: '杯具' },
      { code: 'lighting',  ar: 'إضاءة منزلية',  en: 'Lighting',  zh: '照明' },
    ],
  },
  {
    code: 'building',
    tab:   { ar: 'مواد بناء', en: 'Building', zh: '建材' },
    title: { ar: 'مواد بناء وتشطيب', en: 'Building & finishing', zh: '建材与装修' },
    featureSubs: [
      { code: 'tiles_flooring', ar: 'بلاط وأرضيات', en: 'Tiles & flooring', zh: '瓷砖与地板' },
      { code: 'sanitary',       ar: 'أدوات صحية',   en: 'Sanitary ware',    zh: '卫浴' },
      { code: 'doors_windows',  ar: 'أبواب ونوافذ', en: 'Doors & windows',  zh: '门窗' },
      { code: 'paint',          ar: 'دهانات',       en: 'Paint',            zh: '涂料' },
    ],
  },
  {
    code: 'auto_parts',
    tab:   { ar: 'قطع غيار', en: 'Auto parts', zh: '汽车配件' },
    title: { ar: 'قطع غيار السيارات', en: 'Auto parts', zh: '汽车配件' },
    featureSubs: [
      { code: 'engine_parts',  ar: 'المحرك',       en: 'Engine',    zh: '发动机' },
      { code: 'suspension',    ar: 'التعليق',      en: 'Suspension', zh: '悬挂' },
      { code: 'brakes',        ar: 'الفرامل',      en: 'Brakes',     zh: '制动' },
      { code: 'auto_lighting', ar: 'إضاءة سيارات', en: 'Lighting',   zh: '车灯' },
    ],
  },
];

export const HOME_CATEGORIES = RAW.map((c) => ({
  ...c,
  image: CATEGORY_IMAGE[c.code],
  allSubs: leavesOf(c.code),
}));

// The "mixed" tab (owner, 2026-10-11: "why is it all furniture, it should be
// varied"). Not a category: it has no subcategories and runs no query of its
// own; its cards are drawn from all five categories' rows, one seller each.
// It is the tab the home opens on. Keep identical in the app repo.
export const MIXED_CATEGORY = {
  code: 'mixed',
  tab:   { ar: 'منوّع', en: 'Mixed', zh: '精选' },
  title: { ar: 'منتجات مختارة لتجارتك', en: 'Picks for your trade', zh: '为您精选的产品' },
  image: HOME_CATEGORIES[0].image,
  allSubs: [],
};

// Every leaf across all five, for the single query that feeds the whole section.
export const HOME_SUBCATEGORIES = [
  ...new Set(HOME_CATEGORIES.flatMap((c) => c.allSubs)),
];

// Which category a product belongs to, by its leaf subcategory.
export const CATEGORY_OF_SUB = HOME_CATEGORIES.reduce((acc, c) => {
  c.allSubs.forEach((sub) => { acc[sub] = c.code; });
  return acc;
}, {});

// Self-registered suppliers' products (`products`) carry the supplier
// dashboard's broad `category`, not a leaf subcategory, so they map to a home
// tab here. A category with no entry ('other', 'food', …) stays off the home
// page and shows only in the catalogue (owner, 2026-10-01).
export const HOME_CAT_OF_SUPPLIER_CATEGORY = {
  furniture: 'furniture',
  office_furniture: 'furniture',
  bedroom_furniture: 'furniture',
  kitchen_furniture: 'furniture',
  outdoor_furniture: 'furniture',
  clothing: 'apparel',
  home_decor: 'home_living',
  building: 'building',
  auto_parts: 'auto_parts',
  car_accessories: 'auto_parts',
  tires: 'auto_parts',
  lubricants: 'auto_parts',
};

// How often the visible cards are re-picked while the page is open.
export const HOME_ROTATE_MS = 45000;

// Cards built per category. Four are visible on desktop, so this gives the row
// something to actually carousel through — at four there is nothing to scroll
// and autoplay has nowhere to go. Capped by PER_CATEGORY_FETCH (24) in Home.jsx,
// so raising it costs no extra query.
export const CARDS_PER_CATEGORY = 12;

// A phone shows one card at a time, so twelve is eleven product images fetched
// to be swiped past — on the connection least able to afford them. Home.jsx
// slices the row down to this rather than hiding the surplus in CSS, because a
// hidden <img> is still downloaded. Must match the 720px breakpoint home.css
// uses for the one-card layout.
export const MOBILE_CARDS = 5;
export const MOBILE_QUERY = '(max-width: 720px)';
